// The fuel in an aircraft's tanks, followed flight by flight from what
// people entered: fuel added, the total measured after refuelling, and the
// gauge reading at shutdown. A refuelling is logged on the flight it follows.
// Pure; tested from apps/api/test/domain/fuelState.test.ts.

export interface FuelChainFlight {
  flightId: string
  departure: string
  arrival: string
  flightMinutes: number
  blockMinutes: number
  addedLitres: number | null
  measuredLitres: number | null
  reportedRemainingLitres: number | null
}

export interface FuelModel {
  /** Cruise burn, litres per hour; null: nothing is estimated. */
  burnLitresPerHour: number | null
  /** Start-up, run-up and taxi, litres per ten minutes on the ground. */
  taxiLitresPerTenMin: number
  usableLitres: number | null
}

export type FuelBasis = 'measured' | 'reported' | 'estimated'

export interface FuelState {
  /** In the tanks at off-block; null before the first anchor. */
  startLitres: number | null
  /** In the tanks at shutdown. */
  remainingLitres: number | null
  /** In the tanks after refuelling: what the next flight starts with. */
  leavingLitres: number | null
  basis: FuelBasis | null
  /** An estimate that cannot be trusted: a gap, an impossible figure or fuel not recorded. */
  uncertain: boolean
}

const tenth = (n: number) => Math.round(n * 10) / 10

export function fuelBurnLitres(flight: Pick<FuelChainFlight, 'flightMinutes' | 'blockMinutes'>, model: FuelModel): number | null {
  if (model.burnLitresPerHour === null) return null
  const ground = Math.max(flight.blockMinutes - flight.flightMinutes, 0)
  return (flight.flightMinutes / 60) * model.burnLitresPerHour + (ground / 10) * model.taxiLitresPerTenMin
}

/** `flights` in time order; `startLitres` is what the first one starts with, if known. */
export function fuelChain(
  flights: readonly FuelChainFlight[],
  model: FuelModel,
  startLitres: number | null = null,
): Map<string, FuelState> {
  const states = new Map<string, FuelState>()
  let carry = startLitres
  let carryUncertain = false
  let previousArrival: string | null = null
  for (const f of flights) {
    const gap = previousArrival !== null && previousArrival !== f.departure
    const start = carry
    const burn = fuelBurnLitres(f, model)
    const estimate = start !== null && burn !== null ? start - burn : null

    let remaining: number | null
    let basis: FuelBasis | null
    let uncertain: boolean = carryUncertain || gap
    if (f.reportedRemainingLitres !== null) {
      remaining = f.reportedRemainingLitres
      basis = 'reported'
      uncertain = false
    } else if (f.measuredLitres !== null && f.addedLitres !== null) {
      remaining = f.measuredLitres - f.addedLitres
      basis = 'measured'
      uncertain = false
    } else if (estimate !== null) {
      const impossible = estimate < 0 || (model.usableLitres !== null && estimate > model.usableLitres)
      remaining = Math.max(estimate, 0)
      basis = 'estimated'
      uncertain = uncertain || impossible
    } else {
      remaining = null
      basis = null
    }

    let leaving: number | null
    if (f.measuredLitres !== null) {
      leaving = f.measuredLitres
      uncertain = false
    } else {
      leaving = remaining === null ? null : remaining + (f.addedLitres ?? 0)
      // A skipped fuel field may hide a refuelling.
      if (f.addedLitres === null && basis === 'estimated') uncertain = true
    }

    states.set(f.flightId, {
      startLitres: start === null ? null : tenth(start),
      remainingLitres: remaining === null ? null : tenth(remaining),
      leavingLitres: leaving === null ? null : tenth(leaving),
      basis,
      uncertain,
    })
    carry = leaving
    carryUncertain = uncertain
    previousArrival = f.arrival
  }
  return states
}
